import type { ReactNode } from "react";
import { Database, Table2 } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import type { StepType } from "@/types/workflow.types";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

import { getExtractSummary } from "./extractSummary";
import { getTransformSummary } from "./transformSummary";
import { getLoadSummary } from "./loadSummary";
import { getFilterSummary } from "./filterSummary";
import { getValidateSummary } from "./validateSummary";
import { getLookupSummary } from "./lookupSummary";
import { getAggregateSummary } from "./aggregateSummary";
import { getApiExtractSummary } from "./apiExtractSummary";
import { getApiLoadSummary } from "./apiLoadSummary";
import { getHttpRequestSummary } from "./httpRequestSummary";
import { getNotifySummary } from "./notifySummary";
import { getConditionalSummary } from "./conditionalSummary";
import { getDelaySummary } from "./delaySummary";
import { getCodeSummary } from "./codeSummary";
import { getSubworkflowSummary } from "./subworkflowSummary";
import { getLlmCallSummary } from "./agentic/llm-call-summary";
import { getAgentTaskSummary } from "./agentic/agent-task-summary";
import { getAgentDecisionSummary } from "./agentic/agent-decision-summary";
import { getCheckpointSummary } from "./agentic/checkpoint-summary";
import { getMcpToolCallSummary } from "./agentic/mcp-tool-call-summary";

type SummaryRenderer = (config: Record<string, unknown>, t: SummaryTranslator) => ReactNode[];

const summaryRenderers: Record<string, SummaryRenderer> = {
  extract: getExtractSummary,
  transform: getTransformSummary,
  load: getLoadSummary,
  filter: getFilterSummary,
  validate: getValidateSummary,
  lookup: getLookupSummary,
  aggregate: getAggregateSummary,
  api_extract: getApiExtractSummary,
  api_load: getApiLoadSummary,
  http_request: getHttpRequestSummary,
  notify: getNotifySummary,
  conditional: getConditionalSummary,
  delay: getDelaySummary,
  code: getCodeSummary,
  subworkflow: getSubworkflowSummary,
  // Agentic step summaries
  llm_call: getLlmCallSummary,
  agent_task: getAgentTaskSummary,
  agent_decision: getAgentDecisionSummary,
  checkpoint: getCheckpointSummary,
  mcp_tool_call: getMcpToolCallSummary,
};

/**
 * Get rich summary ReactNodes for a workflow step.
 * Renders shared fields (database, schema, table) first, then step-specific details.
 * Requires a translator function from the calling React component.
 */
export function getStepSummary(
  stepType: StepType,
  config: Record<string, unknown>,
  t: SummaryTranslator
): ReactNode[] {
  const items: ReactNode[] = [];

  // --- Shared: Database / Schema / Table ---
  const databaseName = getString(config.databaseName);
  const schemaName = getString(config.schemaName);
  const tableName = getString(config.tableName);

  if (databaseName) {
    items.push(
      <StepInfoRow key="database" icon={<Database className="size-3.5" />} label={t("summaries.shared.label_database")} value={databaseName} mono />
    );
  }

  if (schemaName) {
    items.push(
      <StepInfoRow key="schema" icon={<Table2 className="size-3.5" />} label={t("summaries.shared.label_schema")} value={schemaName} mono />
    );
  }

  if (tableName) {
    items.push(
      <StepInfoRow key="table" icon={<Table2 className="size-3.5" />} label={t("summaries.shared.label_table")} value={tableName} mono />
    );
  }

  // --- Step-specific ---
  const renderer = summaryRenderers[stepType];
  if (renderer) {
    items.push(...renderer(config, t));
  }

  return items;
}
